import { MessageCircle, FileSignature, Send, BadgeCheck, CalendarClock, ExternalLink } from "lucide-react";

const STEPS = [
  {
    icon: BadgeCheck,
    title: "QUÉ INCLUYE:",
    items: [
      {
        n: "1.",
        h: "Cédula de Identificación Fiscal (CIF)",
        d: "Tu constancia oficial del SAT cuando la necesites.",
      },
      {
        n: "2.",
        h: "Opinión de Cumplimiento SAT (Art. 32-D)",
        d: "Indispensable para licitar, vender a corporativos o validar proveedores.",
      },
      {
        n: "3.",
        h: "Opinión de Cumplimiento IMSS",
        d: "Constancia patronal vigente cuando tu cliente la pida.",
      },
      {
        n: "4.",
        h: "Opinión de Cumplimiento INFONAVIT",
        d: "Constancia laboral actualizada sin trámites en sucursal.",
      },
    ],
    footer: {
      label: "Próximamente: Bot de Facturación.",
      sub: "Emisión y timbrado de CFDI directo en WhatsApp.",
    },
  },
  {
    icon: FileSignature,
    title: "CÓMO FUNCIONA:",
    items: [
      {
        n: "1.",
        h: "Activas tu plan",
        d: "Conversamos por WhatsApp, te orientamos y activamos el servicio según el volumen de tu operación.",
      },
      {
        n: "2.",
        h: "Conectas tu e.firma una sola vez",
        d: "Recibimos tu e.firma con el protocolo de seguridad correspondiente. Es proceso único, no se repite mes a mes.",
      },
      {
        n: "3.",
        h: "Recibes todo por WhatsApp",
        d: "Cada mes (o cuando lo pidas) recibes constancias y opiniones vigentes, listas para usar o reenviar.",
      },
    ],
  },
  {
    icon: Send,
    title: "Plan ajustado a tu volumen de operación. Conversemos.",
  },
];

export default function Plataforma() {
  return (
    <section
      id="plataforma"
      data-testid="plataforma-section"
      className="py-20 sm:py-28 relative overflow-hidden"
      style={{ background: "linear-gradient(180deg, #fafbfc 0%, #f1f5fb 100%)" }}
    >
      <div
        className="absolute top-0 right-0 w-[600px] h-[600px] rounded-full blur-3xl opacity-20 -z-0"
        style={{ background: "radial-gradient(circle, #4776b9, transparent 60%)" }}
      />
      <div className="rp-container relative">
        <div className="grid lg:grid-cols-[1fr_1.05fr] gap-12 items-start">
          <div className="rp-reveal">
            <span className="rp-kicker">Herramientas · Plataforma RP</span>
            <h2 className="mt-4 text-3xl sm:text-4xl lg:text-5xl font-extrabold leading-tight" style={{ color: "var(--rp-ink)" }}>
              Facturación CFDI 4.0 multi-RFC, por <span style={{ color: "#0088CC" }}>Telegram</span>.
            </h2>
            <p className="mt-5 text-base sm:text-lg text-slate-600 leading-relaxed">
              Emite tus facturas desde cualquier lugar conversando con nuestro bot. Sin sistemas complicados,
              sin pantallazos. Multi-RFC para grupos empresariales y despachos.
            </p>
            <p className="mt-3 text-sm sm:text-base font-semibold" style={{ color: "var(--rp-green)" }}>
              Próximamente: constancias y opiniones de cumplimiento.
            </p>

            <div className="mt-8 flex flex-wrap gap-3">
              <a
                href="https://wa.me/5213310902798"
                target="_blank"
                rel="noreferrer"
                data-testid="plataforma-whatsapp-cta"
                className="rp-btn rp-btn-accent"
              >
                <MessageCircle className="w-4 h-4" />
                Pídelo por WhatsApp
              </a>
              <a
                href="https://calendly.com/contadoresrp"
                target="_blank"
                rel="noreferrer"
                data-testid="plataforma-calendly-cta"
                className="rp-btn rp-btn-primary"
              >
                <CalendarClock className="w-4 h-4" />
                Agendar llamada
              </a>
              <a
                href="https://contadoresrp.pro/"
                target="_blank"
                rel="noreferrer"
                data-testid="plataforma-platform-cta"
                className="rp-btn rp-btn-accent"
              >
                <ExternalLink className="w-4 h-4" />
                Entrar a la plataforma
              </a>
            </div>
          </div>

          {/* Steps timeline */}
          <div className="rp-reveal">
            <div
              data-testid="proximamente-heading"
              className="mb-5 inline-flex items-center gap-2 px-4 py-1.5 rounded-full"
              style={{ background: "rgba(50,138,87,0.1)", border: "1px solid rgba(50,138,87,0.25)" }}
            >
              <span
                className="w-2 h-2 rounded-full animate-pulse"
                style={{ background: "var(--rp-green)" }}
              />
              <span
                className="text-xs font-bold uppercase tracking-[0.18em]"
                style={{ color: "var(--rp-green)" }}
              >
                Próximamente
              </span>
            </div>
            <div className="space-y-4">
              {STEPS.map((s, i) => {
                const Icon = s.icon;
                return (
                  <div
                    key={i}
                    data-testid={`platform-step-${i}`}
                    className="rp-card p-6 flex items-start gap-5"
                  >
                    <div
                      className="w-12 h-12 rounded-xl flex items-center justify-center shrink-0"
                      style={{
                        background: "linear-gradient(135deg, var(--rp-blue), var(--rp-green))",
                      }}
                    >
                      <Icon className="w-6 h-6 text-white" />
                    </div>
                    <div className="flex-1">
                      <h4 className="text-lg font-bold" style={{ color: "var(--rp-ink)" }}>{s.title}</h4>
                      {s.text && (
                        <p className="mt-1 text-sm text-slate-600 leading-relaxed">{s.text}</p>
                      )}
                      {s.items && (
                        <ul className="mt-4 space-y-3">
                          {s.items.map((it, k) => (
                            <li key={k} className="flex gap-3">
                              <span
                                className="text-sm font-bold shrink-0"
                                style={{ color: "var(--rp-blue)" }}
                              >
                                {it.n}
                              </span>
                              <div>
                                <div className="text-sm font-semibold" style={{ color: "var(--rp-ink)" }}>
                                  {it.h}
                                </div>
                                <div className="text-sm text-slate-600 leading-relaxed">{it.d}</div>
                              </div>
                            </li>
                          ))}
                        </ul>
                      )}
                      {s.footer && (
                        <div className="mt-5 pt-4 border-t border-slate-100">
                          <div className="text-sm font-bold" style={{ color: "var(--rp-green)" }}>
                            {s.footer.label}
                          </div>
                          <div className="text-sm text-slate-600 mt-0.5">{s.footer.sub}</div>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
